import { useState, type FormEvent, type ReactNode } from 'react';
import { Button, Input } from '@paige/ui';
import { CompanyPicker, type CompanyPickerProps } from '../CompanyPicker';
import type { ProblemSort, ProblemsQuery } from './problem-types';

export interface ProblemsToolbarProps {
  query: ProblemsQuery;
  onQuery: (query: ProblemsQuery) => void;
  /** The one company the rows are of, picked by typing its name (the server answers); absent where there is no picker. */
  company?: CompanyPickerProps | undefined;
}

const SORTS: [ProblemSort, string][] = [
  ['oldest', 'Oldest first'],
  ['largest', 'Largest first'],
  ['company', 'By company'],
];

/** The order of the rows: one of three, the one in force pressed. */
function Order({ query, onQuery }: Pick<ProblemsToolbarProps, 'query' | 'onQuery'>): ReactNode {
  return (
    <div className="pb-bar__sort" role="group" aria-label="Order">
      {SORTS.map(([sort, label]) => (
        <button
          key={sort}
          type="button"
          className="pb-bar__order"
          aria-pressed={query.sort === sort}
          onClick={() => onQuery({ ...query, sort })}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Narrowing and ordering the rows, done by the ledger: a search by company or job name, and the order. Built for a
 * thousand companies: the screen never holds them all, so there is no list of companies to scroll.
 */
export function ProblemsToolbar({ query, onQuery, company }: ProblemsToolbarProps): ReactNode {
  const [text, setText] = useState(query.text);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onQuery({ ...query, text: text.trim() });
  };
  const clear = () => {
    setText('');
    onQuery({ ...query, text: '' });
  };
  return (
    <div className="pb-bar">
      {company ? <CompanyPicker {...company} /> : null}
      <form className="pb-bar__search" role="search" onSubmit={submit}>
        <Input
          aria-label="Company or job name"
          placeholder="Search by company or job name"
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
        {query.text ? (
          <Button type="button" variant="ghost" onClick={clear}>
            Clear
          </Button>
        ) : null}
      </form>
      <Order query={query} onQuery={onQuery} />
    </div>
  );
}
